import type { useFolderActions } from '../../hooks/useFolderActions.ts';
import { IconClose, IconDownload, IconTrash } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';

interface Props {
  selected: string[];
  items: ApiNode[];
  /** 지금 보고 있는 폴더 경로. 루트는 ''. */
  path: string;
  canEdit: boolean;
  actions: ReturnType<typeof useFolderActions>;
  onClear: () => void;
}

/**
 * 항목을 고른 동안 툴바 자리에 나타나는 줄.
 *
 * 별도 줄로 띄우지 않고 툴바를 대체하는 이유: 줄이 하나 늘어나면 목록이
 * 아래로 밀려서, 첫 클릭(선택) 뒤의 두 번째 클릭이 엉뚱한 곳에 떨어진다.
 * 더블클릭으로 폴더를 여는 조작이 그래서 깨진다.
 */
export function SelectionBar({
  selected, items, path, canEdit, actions, onClear,
}: Props) {
  /**
   * 파일 하나만 골랐으면 그 파일을 그대로 내려준다.
   * 여러 개거나 폴더가 섞였으면 zip 으로 묶는다.
   */
  const download = (): void => {
    const onlyFile = selected.length === 1
      && items.find((i) => i.path === selected[0])?.type === 'file';
    if (onlyFile) void actions.download(selected, '선택항목');
    else void actions.downloadZip(selected, '선택항목');
  };

  const isRoot = path === '';

  return (
    <>
      <button className="btn btn--ghost btn--icon" aria-label="선택 해제" onClick={onClear}>
        <IconClose size={16} />
      </button>
      <strong style={{ fontSize: 14 }}>{selected.length}개 선택됨</strong>
      <div className="spacer" />

      <button className="btn" onClick={download}>
        <IconDownload size={16} /> 다운로드
      </button>

      <button
        className="btn"
        disabled={!canEdit || isRoot}
        title={isRoot ? '이미 최상위 폴더입니다' : '상위 폴더로 이동'}
        onClick={() => {
          actions.move.mutate({
            paths: selected, targetFolder: path.slice(0, path.lastIndexOf('/')),
          });
        }}
      >
        위로 이동
      </button>

      <button
        className="btn btn--danger"
        disabled={!canEdit}
        onClick={() => { actions.remove.mutate(selected); }}
      >
        <IconTrash size={16} /> 삭제
      </button>
    </>
  );
}
